import { db, functions, httpsCallable, storage } from '@util/firebase';
import {
  arrayRemove,
  doc,
  orderBy,
  query,
  startAfter,
  updateDoc,
} from '@firebase/firestore';
import {
  collection,
  CollectionReference,
  setDoc,
  where,
  getDoc,
  limit,
  getDocs,
} from 'firebase/firestore';
import { ChatDocument, ChatMessageDocument } from '@util/types/firestore/chat';
import { Role } from '@util/types/firestore/users';
import { requestChatMaskProof } from './mask-proof';
import { ref, uploadBytes } from 'firebase/storage';
import { ContactUsForm } from 'pages/contact-us';
import { OrderDocument } from '@util/types/firestore/order';
import {
  handleContactInfoDetection,
  detectAndRedactContactInfo,
} from './contact-info-detection';

export const messagesRef = collection(
  db,
  'messages'
) as CollectionReference<ChatDocument>;

export function getChatsByUidQuery(uid: string): ReturnType<typeof query> {
  return query(
    messagesRef,
    where('uids', 'array-contains', uid),
    orderBy('last_time', 'desc')
  );
}

export function getChatsByChatIds(chatIds: string[]): ReturnType<typeof query> {
  return query(
    messagesRef,
    where('id', 'in', chatIds),
    orderBy('last_time', 'desc')
  );
}

export function getChatById(chat_id: string): ReturnType<typeof doc> {
  return doc(messagesRef, chat_id);
}

export async function getChatDocumentById(chat_id: string): Promise<ChatDocument | null> {
  const snapshot = await getDoc(getChatById(chat_id));
  if (!snapshot.exists()) {
    return null;
  }
  return snapshot.data() as ChatDocument;
}

export async function getChatsForOrderDetails(order: OrderDocument): Promise<ChatDocument[]> {
  const promises = [getChatsByOrderId(order.id)];
  if (order.product_ids && order.seller_arr)
    promises.push(
      getChatsByProductIds(order.product_ids, order.seller_arr, order.buyer_id)
    );
  return (await Promise.all(promises))
    .flat()
    .sort((a, b) => b.last_time - a.last_time);
}

async function getChatsByProductIds(
  products: string[],
  seller_ids: string[],
  buyer_id: string
): Promise<ChatDocument[]> {
  const q = query(
    messagesRef,
    where('product_id', 'in', products),
    where('seller_id', 'in', seller_ids),
    where('buyer_id', '==', buyer_id)
    // orderBy('last_time', 'desc') // dont wanna create index
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

async function getChatsByOrderId(order_id: string): Promise<ChatDocument[]> {
  const q = query(
    messagesRef,
    where('order_id', '==', order_id),
    orderBy('last_time', 'desc')
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

async function fetchFilteredChats(afterTime?: number): Promise<ChatDocument[]> {
  const PAGE_SIZE = 20;
  const BATCH_SIZE = 60;
  const results: ChatDocument[] = [];
  let cursor = afterTime;

  while (results.length < PAGE_SIZE) {
    const q = cursor
      ? query(messagesRef, orderBy('last_time', 'desc'), startAfter(cursor), limit(BATCH_SIZE))
      : query(messagesRef, orderBy('last_time', 'desc'), limit(BATCH_SIZE));

    const snapshot = await getDocs(q);

    if (snapshot.empty) break;

    const batch = snapshot.docs.map((doc) => doc.data());
    const filtered = batch.filter((chat) => chat.buyer_id !== 'GearBot');
    results.push(...filtered);

    // If we got fewer than BATCH_SIZE, there's no more data
    if (snapshot.docs.length < BATCH_SIZE) break;

    // Move cursor to last item in the raw batch (not filtered)
    cursor = batch[batch.length - 1].last_time;
  }

  return results.slice(0, PAGE_SIZE);
}

export async function getTwentyChats(lastChatTime?: number): Promise<ChatDocument[]> {
  if (lastChatTime) {
    return await getNextTwenty(lastChatTime);
  }
  return await fetchFilteredChats();
}

export async function getNextTwenty(lastChatTime: number): Promise<ChatDocument[]> {
  return await fetchFilteredChats(lastChatTime);
}

export async function sendMessageToChat(
  message: ChatMessageDocument,
  chat: ChatDocument,
  currUserId?: string,
  participantNames?: string[],
  // GB #1942: the SENDER's roles, from the signed-in user's own users/{uid}
  // doc (useAuth().userDoc.roles). Exempt senders skip masking. Never derive
  // this from the message; the server re-checks, so this is a convenience.
  senderRoles?: Role | null
): Promise<void> {
  if (!message) return;

  const { SUPPORT_ID } = await import('./rate-limit');
  const isSupportChat = chat.uids?.includes(SUPPORT_ID) || chat.buyer_id === SUPPORT_ID || chat.seller_id === SUPPORT_ID;
  if (!isSupportChat) {
    const { assertMessagingAllowed } = await import('./messaging-pause');
    await assertMessagingAllowed(message.uid);
    // The lookup may take three seconds. Read after it so a reply or server
    // redaction arriving during that wait is not overwritten by captured UI state.
    const latestChat = chat.id ? await getChat(chat.id) : null;
    if (!latestChat) throw new Error('Conversation unavailable. Please try again.');
    chat = latestChat;
  }

  // Detect and redact contact info, send alerts if detected
  const detectionResult = await handleContactInfoDetection(
    message.content,
    chat,
    message.uid,
    participantNames,
    senderRoles
  );
  const proofId = await requestChatMaskProof({
    chatId: chat.id ?? '', senderUid: message.uid,
    rawContent: detectionResult.originalContent, wasRedacted: detectionResult.wasRedacted,
  });
  if (proofId) {
    // Proof creation also waits on the network. Preserve replies arriving during it.
    const latestChat = chat.id ? await getChat(chat.id) : null;
    if (!latestChat) throw new Error('Conversation unavailable. Please try again.');
    chat = latestChat;
  }
  delete message.mask_proof_id;
  if (proofId) message.mask_proof_id = proofId;
  message.content = detectionResult.redactedContent;
  message.had_nonallowlisted_link =
    detectionResult.detectedTypes.includes('suspicious_url');
  message.had_fraud_mask = detectionResult.wasRedacted;
  message.fraud_mask_types = detectionResult.wasRedacted
    ? detectionResult.detectedTypes.filter(
        (type) => type !== 'social_platform' && type !== 'offplatform_mention'
      )
    : [];

  // Flag chat if contact info was detected
  if (detectionResult.wasRedacted && chat.id) {
    flagChat(chat.id, true);
  }

  const newChat = { ...chat };
  if (!newChat.unread) {
    newChat.unread = {};
  }

  if (
    message.uid !== process.env.NEXT_PUBLIC_SUPPORT_ID &&
    chat.uids &&
    currUserId
  ) {
    const recipient = currUserId === chat.uids[0] ? chat.uids[1] : chat.uids[0];
    newChat.unread = { [recipient]: true, [currUserId]: false };
  }

  if (newChat.messages) {
    newChat.messages.push(message);
  } else {
    newChat.messages = [message];
  }
  const now = Date.now();
  message.created_at = now;
  newChat.last_time = now;
  await setDoc(doc(messagesRef, chat.id), newChat);
}

export function allocateChatId(): string {
  return doc(messagesRef).id;
}

export async function createChat(chat: ChatDocument): Promise<string> {
  const firstSenderUid = chat.messages?.[0]?.uid;
  const { SUPPORT_ID } = await import('./rate-limit');
  const isSupportChat = chat.uids?.includes(SUPPORT_ID) ||
    chat.buyer_id === SUPPORT_ID || chat.seller_id === SUPPORT_ID;
  if (firstSenderUid && !(chat.is_bot && firstSenderUid === 'GearBot') && !isSupportChat) {
    const { assertMessagingAllowed } = await import('./messaging-pause');
    await assertMessagingAllowed(firstSenderUid);
  }
  const docRef = chat.id ? doc(messagesRef, chat.id) : doc(messagesRef);
  const now = Date.now();
  chat.id = docRef.id;
  chat.last_time = now;
  chat.created_at = now;
  await setDoc(docRef, chat);
  return docRef.id;
}

export async function getChat(chat_id: string): Promise<ChatDocument | null> {
  const docRef = doc(messagesRef, chat_id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as ChatDocument;
  } else {
    return null;
  }
}

export async function updateChat(chat: ChatDocument): Promise<void> {
  if (!chat.id) throw new Error('Chat ID is required');
  const docRef = doc(messagesRef, chat.id);
  await updateDoc(docRef, chat);
}

export async function getNextCaseNumber() {
  // get highest case_num from messages, add 1
  const q = query(
    messagesRef,
    orderBy('case_num', 'desc'),
    where('case_num', '!=', null),
    limit(1)
  );
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return 1;
  }
  const doc = querySnapshot.docs[0];
  const data = doc.data();
  return (data.case_num ?? 0) + 1;
}

export async function markAsRead(chat_id: string, uid: string): Promise<void> {
  const docRef = doc(messagesRef, chat_id);
  const key = `unread.${uid}`;
  await updateDoc(docRef, { [key]: false });
}

export async function flagChat(chat_id: string, flagged: boolean): Promise<void> {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { flagged });
}

export async function hideChat(chat_id: string, uid: string): Promise<void> {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { uids: arrayRemove(uid) });
}

export async function deleteMessage(
  chat_id: string,
  message: ChatMessageDocument
): Promise<void> {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { messages: arrayRemove(message) });
}

export async function uploadChatImage(
  data: Blob,
  user_id: string,
  chat_id: string
): Promise<string> {
  const path = `${user_id}/messages/${chat_id}/${Date.now()}`;
  const imagesRef = ref(storage, path);
  const res = await uploadBytes(imagesRef, data);
  return res.metadata.fullPath;
}

export async function contactUs(form: ContactUsForm) {
  const res = await httpsCallable<ContactUsForm, { data: 'success' }>(
    functions,
    'contactUs'
  )(form);
  return res.data.data;
}

/**
 * Redacts contact info from a string (phones, emails, social handles, payment apps)
 * For simple redaction without alerts - use handleContactInfoDetection for full functionality
 * @deprecated Use handleContactInfoDetection for new code - it includes alerts
 */
export function redactPhonesAndEmailsFromStr(str: string, chat?: ChatDocument): string {
  const result = detectAndRedactContactInfo(str);

  // Flag chat if contact info was detected (legacy behavior)
  if (result.wasRedacted && chat?.id) {
    flagChat(chat.id, true);
  }

  return result.redactedContent;
}
